'use client';

// ViralFooter - Viral CTA footer with A/B testing variants
// Story 4.6: Viral CTA - Create Your Own Boty
// Story 6.5: Viral Tracking and Attribution
// Story 6.11: Viral CTA A/B Testing

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import { BotyIcon } from '@boty/ui';
import { ensureProtocol } from '../../lib/ensureProtocol';
import { ConsumerFeedbackLink } from './ConsumerFeedbackLink';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useAutoLocale } from '../../lib/useAutoLocale';
import {
  useViralCTAVariant,
  VARIANT_I18N_KEYS,
} from '../../lib/viralCTAVariants';
import {
  trackViralCTAImpression,
  trackViralCTAClick,
  trackViralBadgeClick,
} from '../../lib/viralTracking';

interface ViralFooterProps {
  /** Bot ID for referral attribution */
  botId?: string;
  /** Creator ID for tracking */
  creatorId?: string | undefined;
}

export function ViralFooter({
  botId,
  creatorId,
}: ViralFooterProps) {
  const t = useTranslations('consumer.bot');
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';
  const pathname = usePathname();
  const locale = pathname?.split('/')[1] || 'en';
  const rawCreatorUrl = process.env.NEXT_PUBLIC_CREATOR_URL;
  const creatorUrl = rawCreatorUrl ? ensureProtocol(rawCreatorUrl) : undefined;

  useAutoLocale();

  const { variant } = useViralCTAVariant();
  const impressionFired = useRef(false);

  // Fire impression event once on mount
  useEffect(() => {
    if (!impressionFired.current) {
      impressionFired.current = true;
      trackViralCTAImpression(botId, variant, creatorId);
    }
  }, [botId, variant, creatorId]);

  // Don't render if creator URL is not configured
  if (!creatorUrl) {
    return null;
  }

  // Build URL with UTM params, variant, and referral attribution (Story 6.5, 6.11)
  const refParam = botId ? `&bot_id=${botId}` : '';
  const variantParam = `&variant=${variant}`;
  const ctaUrl = `${creatorUrl}?utm_source=bot&utm_medium=footer&utm_campaign=viral&ref=viral_cta${variantParam}${refParam}`;

  const handleCTAClick = () => {
    trackViralCTAClick(botId, variant, creatorId);
  };

  const handleBadgeClick = () => {
    trackViralBadgeClick(botId);
  };

  // CTA i18n key based on variant
  const ctaText = t(VARIANT_I18N_KEYS[variant]);

  return (
    <Box
      component="footer"
      sx={{
        py: { xs: 1, sm: 1.5 },
        px: { xs: 1.5, sm: 2 },
        pb: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
        textAlign: 'center',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Viral CTA - always visible */}
      <Link
        href={ctaUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleCTAClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          color: 'secondary.main',
          fontSize: { xs: 12, sm: 14 },
          textDecoration: 'none',
          minHeight: 48,
          px: 2,
          '@media (hover: hover)': {
            '&:hover': { textDecoration: 'underline' },
          },
          '&:active': {
            transform: 'scale(0.98)',
            opacity: 0.9,
          },
          touchAction: 'manipulation',
        }}
      >
        <BotyIcon size={20} variant="simple" />
        <Typography
          variant="body2"
          component="span"
          sx={{ fontSize: 'inherit' }}
        >
          {ctaText}
        </Typography>
        <OpenInNewIcon
          sx={{
            fontSize: 14,
            transform: isRtl ? 'scaleX(-1)' : 'none',
          }}
        />
      </Link>

      {/* Powered by badge + samples + feedback link */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mt: 0.5,
        }}
      >
        <Link
          href={ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleBadgeClick}
          sx={{
            display: 'inline-block',
            color: 'text.secondary',
            fontSize: 10,
            textDecoration: 'none',
            opacity: 0.6,
            '@media (hover: hover)': {
              '&:hover': { opacity: 1 },
            },
          }}
        >
          {t('poweredByBoty')}
        </Link>
        <Typography
          component="span"
          sx={{ color: 'text.secondary', fontSize: 10, opacity: 0.4 }}
        >
          |
        </Typography>
        <Link
          href={`/${locale}/samples`}
          sx={{
            display: 'inline-block',
            color: 'text.secondary',
            fontSize: 10,
            textDecoration: 'none',
            opacity: 0.6,
            '@media (hover: hover)': {
              '&:hover': { opacity: 1 },
            },
          }}
        >
          Samples
        </Link>
        <Typography
          component="span"
          sx={{ color: 'text.secondary', fontSize: 10, opacity: 0.4 }}
        >
          |
        </Typography>
        <ConsumerFeedbackLink botId={botId} />
        <Typography
          component="span"
          sx={{ color: 'text.secondary', fontSize: 10, opacity: 0.4 }}
        >
          |
        </Typography>
        <LanguageSwitcher />
      </Box>
    </Box>
  );
}
